import React, { useEffect, useRef, useState } from 'react'
import { ControlledBoard, KanbanBoard } from '@caldwell619/react-kanban'
import '@caldwell619/react-kanban/dist/styles.css'
import {
  BoardContainer,
  CardContainer,
  CardHeader,
  Avatar,
  CardTitle,
  CardTag,
  CardBadge,
  CardInfo,
  CardValue,
  CardTime,
  ColumnHeader,
  ColumnTitle,
  ActionButtonsContainer,
  ActionButton,
  ContentFiltros
} from './style'
import { FaWhatsapp, FaPhone, FaEnvelope, FaClock } from 'react-icons/fa'
import { Badge, Button, Col, Row } from 'reactstrap'
import { maskMoney, maskPhoneWithDDD } from 'util/masks'
import InputCustom from 'components/Inputs'
import CustomSelect from 'components/Select'
import { CiFilter } from 'react-icons/ci'
import { MdOutlineWatchLater } from 'react-icons/md'
import { CardKanban } from 'store/modules/kanban/types'

interface KanbanProps {
  data: KanbanBoard<CardKanban>
}

const KanbanComponent = ({ data }: KanbanProps) => {
  const [searchText, setSearchText] = useState<string>('')
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [showTagsDropdown, setShowTagsDropdown] = useState<boolean>(false)
  const [isOverDue, setIsOverDue] = useState<boolean>(false)
  const [board, setBoard] = useState<KanbanBoard<CardKanban>>(data)

  const calculateTimespan = (time: string): string => {
    const now = new Date()
    const pastDate = new Date(time)
    const diff = Math.abs(now.getTime() - pastDate.getTime())

    const years = Math.floor(diff / (1000 * 60 * 60 * 24 * 365))
    const months = Math.floor((diff / (1000 * 60 * 60 * 24 * 30)) % 12)
    const days = Math.floor((diff / (1000 * 60 * 60 * 24)) % 30)
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24)
    const minutes = Math.floor((diff / (1000 * 60)) % 60)

    if (years > 0) return `há ${years} ano${years > 1 ? 's' : ''}`
    if (months > 0) return `há ${months} mês${months > 1 ? 'es' : ''}`
    if (days > 0) return `há ${days} dia${days > 1 ? 's' : ''}`
    if (hours > 0) return `há ${hours} hora${hours > 1 ? 's' : ''}`
    return `há ${minutes} minuto${minutes > 1 ? 's' : ''}`
  }

  const filteredBoard = {
    ...board,
    columns: board?.columns.map((column) => ({
      ...column,
      cards: column.cards
        .filter(
          (card) =>
            card.title.toLowerCase().includes(searchText.toLowerCase()) ||
            card.email.toLowerCase().includes(searchText.toLowerCase()) ||
            card.phone.includes(searchText) ||
            card.cpf.includes(searchText)
        )
        .filter((card) =>
          selectedTags.length > 0
            ? selectedTags.includes(card.tag.toLowerCase())
            : true
        )
    }))
  }

  type DragPosition = { fromPosition: number; fromColumnId: string | number }
  type DropPosition = { toPosition: number; toColumnId: string | number }
  const handleCardDragEnd = (
    card: CardKanban,
    from: DragPosition,
    to: DropPosition
  ) => {
    setBoard((prevBoard) => {
      if (!prevBoard) return prevBoard
      const newColumns = prevBoard.columns.map((col) => {
        if (col.id === from.fromColumnId) {
          const newCards = [...col.cards]
          newCards.splice(from.fromPosition, 1)
          return { ...col, cards: newCards }
        }
        return col
      })

      const destColumnIndex = newColumns.findIndex(
        (c) => c.id === to.toColumnId
      )
      const destColumn = newColumns[destColumnIndex]
      const newDestCards = [...destColumn.cards]
      newDestCards.splice(to.toPosition, 0, card)
      newColumns[destColumnIndex] = { ...destColumn, cards: newDestCards }

      return { ...prevBoard, columns: newColumns }
    })
  }

  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowTagsDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div className="d-flex flex-column gap-3">
      <ContentFiltros>
        <form autoComplete="off">
          <InputCustom
            type="text"
            placeholder="Buscar por nome, empresa, CPF ou telefone..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </form>
        <div className="d-flex gap-2 flex-wrap">
          <div>
            <CustomSelect
              options={[
                { value: 'all', label: 'Todos' },
                { value: 'high', label: 'Alta Prioridade' },
                { value: 'medium', label: 'Média Prioridade' },
                { value: 'low', label: 'Baixa Prioridade' }
              ]}
              accessorLabel="label"
              accessorValue="value"
              placeholder="Todas"
              width={200}
              isClearable
            />
          </div>
          <div>
            <CustomSelect
              options={[
                { value: '1', label: 'Inss' },
                { value: '2', label: 'Servidor Público Municipal' },
                { value: '3', label: 'Servidor Público Estadual' },
                { value: '4', label: 'Servidor Público Federal' },
                { value: '5', label: 'CLT' },
                { value: '6', label: 'Bolsa Família' }
              ]}
              accessorLabel="label"
              accessorValue="value"
              placeholder="Todos os Perfis"
              width={200}
              isClearable
            />
          </div>
          <div>
            <Button
              className="btn-filtro"
              onClick={() => setIsOverDue((prev) => !prev)}
              active={isOverDue}
            >
              <MdOutlineWatchLater />
              <span>Atrasadas</span>
            </Button>
          </div>
          <div>
            <Button
              className="btn-filtro"
              onClick={() => setShowTagsDropdown((prev) => !prev)}
              active={selectedTags.length > 0}
            >
              <CiFilter />
              <span>Tags</span>
            </Button>
            {showTagsDropdown && (
              <div ref={dropdownRef} className="tags-dropdown">
                <p>Filtrar por tags</p>
                {[
                  'fgts',
                  'saque',
                  'ex-clt',
                  'novo',
                  'autônoma',
                  'autonomo',
                  'motorista',
                  'bolsa-familia'
                ].map((tag) => (
                  <div key={tag}>
                    <input
                      type="checkbox"
                      checked={selectedTags.includes(tag)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTags((prev) => [...prev, tag])
                        } else {
                          setSelectedTags((prev) =>
                            prev.filter((t) => t !== tag)
                          )
                        }
                      }}
                    />
                    <label>{tag}</label>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </ContentFiltros>
      <BoardContainer>
        <ControlledBoard
          onCardDragEnd={(card, from, to) => {
            if (
              card === undefined ||
              from?.fromColumnId === undefined ||
              from?.fromPosition === undefined ||
              to?.toColumnId === undefined ||
              to?.toPosition === undefined
            ) {
              return
            }

            handleCardDragEnd(
              card,
              {
                fromPosition: from.fromPosition,
                fromColumnId: from.fromColumnId
              },
              {
                toPosition: to.toPosition,
                toColumnId: to.toColumnId
              }
            )
          }}
          disableColumnDrag
          allowRemoveColumn={false}
          allowRemoveCard={false}
          allowRenameColumn={false}
          allowAddCard={false}
          renderCard={(card) => (
            <CardContainer borderColor={card.borderColor}>
              <CardHeader>
                <div className="d-flex align-items-center gap-2">
                  <Avatar>
                    <FaWhatsapp color="#25d366" size={15} />
                  </Avatar>
                  <div className="d-flex flex-column gap-1">
                    <CardTitle>{card.title}</CardTitle>
                    <CardTag color={card.beneficioColor}>{card.tag}</CardTag>
                  </div>
                </div>
                <CardBadge color={card.priorityColor}>{card.badge}</CardBadge>
              </CardHeader>
              <Row>
                <Col className="d-flex align-items-center gap-2">
                  <FaPhone size={12} />
                  <CardInfo>{maskPhoneWithDDD(card.phone)}</CardInfo>
                </Col>
              </Row>
              <Row>
                <Col className="d-flex align-items-center gap-2">
                  <FaEnvelope size={12} /> <CardInfo>{card.email}</CardInfo>
                </Col>
              </Row>
              <Row>
                <Col className="d-flex align-items-center gap-2">
                  <CardValue>
                    R$&nbsp;
                    {card.value
                      ? maskMoney(card.value.toFixed(2))
                      : maskMoney(0)}
                  </CardValue>
                </Col>
                <Col className="d-flex align-items-center justify-content-end gap-2">
                  <FaClock size={12} />
                  <CardTime>{calculateTimespan(card.time)}</CardTime>
                </Col>
              </Row>
              <ActionButtonsContainer className="action-buttons-container">
                <ActionButton>Ligar</ActionButton>
                <ActionButton>WA</ActionButton>
                <ActionButton>Email</ActionButton>
              </ActionButtonsContainer>
            </CardContainer>
          )}
          renderColumnHeader={(column) => (
            <ColumnHeader>
              <ColumnTitle>{column.title}</ColumnTitle>
              <Badge color="success" pill>
                {column.cards.length}
              </Badge>
            </ColumnHeader>
          )}
        >
          {filteredBoard}
        </ControlledBoard>
      </BoardContainer>
    </div>
  )
}

export default KanbanComponent
